import { STATUS_COLORS, STATUS_LABELS } from '@/utils/constants';
import { getImageUrl } from '@/utils/api';
import { Clock, AlertCircle, MessageCircle, AlertTriangle } from 'lucide-react';

const TaskListItem = ({ task, isSelected, onClick, currentUserId }) => {
  const getStatusColor = (status) => {
    return STATUS_COLORS[status] || STATUS_COLORS.pending;
  };

  const getStatusLabel = (status) => {
    return STATUS_LABELS[status] || status;
  };

  const imageUrl = getImageUrl(task.product_image);
  
  // Check if task has unread messages for current user
  const hasUnread = task.unread_by?.includes(currentUserId);
  
  // Format due date
  const formatDueDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return { text: `${Math.abs(diffDays)}d overdue`, color: 'text-red-600' };
    if (diffDays === 0) return { text: 'Due today', color: 'text-orange-600' };
    if (diffDays === 1) return { text: 'Due tomorrow', color: 'text-amber-600' };
    if (diffDays <= 7) return { text: `${diffDays}d left`, color: 'text-blue-600' };
    return { text: date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }), color: 'text-slate-500' };
  };

  const dueInfo = formatDueDate(task.target_date);

  return (
    <div
      onClick={onClick}
      className={`p-3 cursor-pointer transition-colors hover:bg-muted border-b border-border/50 relative ${
        isSelected ? 'bg-muted' : ''
      }`}
      style={isSelected ? { borderLeft: '3px solid hsl(45, 90%, 50%)' } : {}}
      data-testid={`task-item-${task.id}`}
    >
      {/* Unread indicator */}
      {hasUnread && (
        <div className="absolute top-2 right-2">
          <span className="flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: 'hsl(45, 90%, 50%)' }}></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ backgroundColor: 'hsl(45, 90%, 50%)' }}></span>
          </span>
        </div>
      )}

      <div className="flex gap-3">
        {/* Image thumbnail */}
        <div className="rounded-md overflow-hidden bg-muted flex-shrink-0" style={{ width: '48px', height: '48px' }}>
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={task.product_name || task.client_name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground" style={{ fontSize: '8px' }}>
              No Image
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="font-medium text-foreground text-sm truncate leading-tight">
                {(task.priority === 'super_rush' || task.priority === 'urgent') && <AlertTriangle size={14} className="inline-block mr-1 text-red-500 fill-red-100 animate-pulse" title="SUPER RUSH" />}
                {task.style_no || task.party_cad_no || task.internal_cad_no}
                {task.design_status === 'cad_completed' && <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-500 text-white text-[8px] font-bold animate-pulse" title="CAD Ready for Review">!</span>}
              </h3>
              <p className="text-xs text-muted-foreground truncate">
                {task.client_name}{task.product_name ? ` — ${task.product_name}` : ''}
              </p>
            </div>
          </div>
          
          {/* CAD No & Metal */}
          <div className="mt-1 flex items-center gap-2 text-xs">
            <span className="text-muted-foreground font-mono">{task.internal_cad_no?.slice(-12)}</span>
            {task.metal_type && (
              <span className="text-muted-foreground">• {task.metal_type} {task.metal_colour}</span>
            )}
          </div>

          {/* Bottom row: Status, Due Date, Assignee */}
          <div className="mt-1.5 flex items-center justify-between gap-2">
            <span className={`text-xs px-1.5 py-0.5 rounded border ${getStatusColor(task.design_status)}`}>
              {getStatusLabel(task.design_status)}
            </span>
            
            <div className="flex items-center gap-2">
              {dueInfo && (
                <span className={`text-xs flex items-center gap-1 ${dueInfo.color}`}>
                  {dueInfo.color === 'text-red-600' ? <AlertCircle size={10} /> : <Clock size={10} />}
                  {dueInfo.text}
                </span>
              )}
              {task.designer_name && (
                <span className="text-xs text-muted-foreground truncate max-w-[60px]" title={task.designer_name}>
                  {task.designer_name.split(' ')[0]}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskListItem;
